import React from 'react';
import { 
  Security, 
  Policy, 
  Payment, 
  LocalShipping, 
  AccountCircle, 
  Update, 
  SupportAgent,
  PrivacyTip,
  GppGood,
  VerifiedUser,
  Shield,
  Lock,
  Assignment,
  Description
} from '@mui/icons-material';

const PoliciesPageEN: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8" dir="ltr">
        
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-gradient-to-r from-primary to-primary-dark p-5 rounded-2xl shadow-xl">
                <Policy className="text-white text-4xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Platform Policies – Bazaar Marketplace
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-primary to-primary-dark mx-auto rounded-full mb-4"></div>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Last Updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Introduction Card */}
        <div className="mb-10 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-l-4 border-primary shadow-sm">
          <div className="flex items-start gap-4">
            <Shield className="text-primary text-2xl mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Welcome to Bazaar</h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                Your use of our platform constitutes explicit acceptance of the following policies. 
                These policies clarify the obligations of different parties, their rights, and how we handle data and services.
                We are committed to providing a safe and transparent experience for all users.
              </p>
            </div>
          </div>
        </div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {[
            {
              icon: <PrivacyTip />,
              title: "1. Privacy and Data Protection Policy",
              items: [
                "We collect user data only during registration or purchase transactions.",
                "We use data to improve user experience, manage orders, and communicate effectively.",
                "We do not sell or share data with any third party without explicit consent.",
                "All data is stored securely and protected under Egyptian data protection laws.",
                "Users have the right to request deletion of their personal data at any time.",
                "We use SSL encryption to protect data during transmission over the internet."
              ],
              color: "from-blue-500/20 to-blue-600/20",
              borderColor: "border-blue-500"
            },
            {
              icon: <Description />,
              title: "2. Content and Advertising Policy",
              items: [
                "Posting any content that violates laws or contains misleading or fraudulent information is prohibited.",
                "Displayed products must be genuine and match the description.",
                "Any inappropriate content is removed immediately without prior notice.",
                "Use of unauthorized or copyrighted images is prohibited.",
                "Prices must be clear and include all additional costs.",
                "Content is reviewed before publishing to ensure quality and relevance."
              ],
              color: "from-purple-500/20 to-purple-600/20",
              borderColor: "border-purple-500"
            },
            {
              icon: <Payment />,
              title: "3. Payment and Refund Policy",
              items: [
                "Payment is made through secure electronic means (credit cards, Fawry, digital wallets).",
                "Customers can request product returns within 14 days of receipt in case of defects.",
                "Return processes are handled by our customer service, in coordination with the selling company.",
                "Amounts are refunded within 5-7 working days of return confirmation.",
                "Returned products must be in their original condition and packaging.",
                "Return policy does not cover products damaged due to misuse."
              ],
              color: "from-green-500/20 to-green-600/20",
              borderColor: "border-green-500"
            },
            {
              icon: <LocalShipping />,
              title: "4. Delivery and Shipping Policy",
              items: [
                "We use trusted shipping companies to ensure product delivery as quickly as possible.",
                "Order status can be tracked directly through the customer account.",
                "Delivery time depends on customer location and product nature.",
                "Shipping costs are determined based on weight, distance, and shipping method.",
                "Customers are notified of expected delivery date upon order confirmation.",
                "For administrative units and remote areas, delivery may take 2-3 additional days."
              ],
              color: "from-orange-500/20 to-orange-600/20",
              borderColor: "border-orange-500"
            },
            {
              icon: <AccountCircle />,
              title: "5. Account Management Policy",
              items: [
                "Users have complete freedom to modify their data through the control panel.",
                "We reserve the right to suspend or cancel accounts in case of policy violations.",
                "Users are responsible for maintaining the confidentiality of their login credentials.",
                "Personal information must be accurate and up-to-date.",
                "Accounts can be closed at user request.",
                "Account records are retained for 5 years for legal and tax purposes."
              ],
              color: "from-red-500/20 to-red-600/20",
              borderColor: "border-red-500"
            },
            {
              icon: <GppGood />,
              title: "6. Security and Protection Policy",
              items: [
                "We use advanced protection systems against hacking and cyber attacks.",
                "All financial transactions are encrypted using SSL/TLS technologies.",
                "We perform regular data backups.",
                "Our team is trained to handle security incidents.",
                "Users are immediately notified in case of any security breach.",
                "We comply with PCI DSS standards for payment data security."
              ],
              color: "from-indigo-500/20 to-indigo-600/20",
              borderColor: "border-indigo-500"
            },
            {
              icon: <Update />,
              title: "7. Updates and Modifications",
              note: "We reserve the right to modify these policies at any time. Users will be notified of significant changes via email or in-platform notifications. Continued use of the platform after updates means acceptance of the new policies. We recommend periodically reviewing this page for the latest updates."
            },
            {
              icon: <SupportAgent />,
              title: "8. Support and Communication Policy",
              note: (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">Email:</span>
                    <a href="mailto:support@bazaar-marketplace.com" className="text-primary hover:underline font-medium">
                      support@bazaar-marketplace.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">WhatsApp:</span>
                    <a href="https://wa.me/201503323006" className="text-primary hover:underline font-medium">
                      +20 150 332 3006
                    </a>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">Working Hours:</span>
                    <span className="text-gray-700">10 AM - 10 PM (Egypt Time)</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <span className="text-primary font-bold">Developer:</span>
                    <a href="https://codeocean.tech" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                      CODEOCEAN.TECH
                    </a>
                  </div>
                  <p className="text-gray-600 mt-2">
                    Average response time: Within 4 working hours. We commit to resolving issues within 48 hours maximum.
                  </p>
                </div>
              )
            }
          ].map((section, index) => (
            <section 
              key={index} 
              className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100"
            >
              <div className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`p-4 rounded-xl bg-gradient-to-r ${section.color}`}>
                    <span className="text-gray-800 text-2xl">{section.icon}</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                      {section.title}
                      <span className={`h-1 flex-1 bg-gradient-to-r ${section.borderColor} to-transparent rounded-full`}></span>
                    </h2>
                    
                    {section.items && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <VerifiedUser className="text-primary text-sm mt-1 flex-shrink-0" />
                            <span className="text-gray-700 leading-relaxed">{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {section.note && (
                      <div className="mt-4 p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border-l-4 border-primary">
                        {typeof section.note === 'string' ? (
                          <p className="text-gray-700 leading-relaxed text-lg">{section.note}</p>
                        ) : (
                          section.note
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {section.borderColor && (
                <div className={`h-1 w-full bg-gradient-to-r ${section.borderColor} to-transparent`}></div>
              )}
            </section>
          ))}
        </div>

        {/* Important Notice */}
        <div className="mt-12 p-6 bg-gradient-to-r from-gray-900 to-black rounded-2xl text-white">
          <div className="flex items-center gap-4 mb-4">
            <Lock className="text-white text-2xl" />
            <h3 className="text-xl font-bold">Important Notes</h3>
          </div>
          <div className="space-y-3">
            <p className="text-gray-200">
              ⚠️ These policies are an integral part of the terms of use and are legally binding.
            </p>
            <p className="text-gray-200">
              📝 We recommend saving a copy of these policies or printing them for reference when needed.
            </p>
            <p className="text-gray-200">
              🔒 Violation of any of these policies may lead to account suspension or legal action.
            </p>
            <p className="text-gray-200">
              📞 For legal inquiries, please contact the support team for assistance.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <div className="mb-6">
            <div className="inline-flex flex-wrap items-center justify-center gap-3 bg-gradient-to-r from-primary/10 to-primary/20 px-6 py-4 rounded-2xl">
              <span className="text-primary font-bold text-lg">Bazaar Marketplace</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">Tax ID: 752-382-462</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">Commercial Register: 123456</span>
            </div>
          </div>
          
          <div className="text-gray-600 text-sm leading-relaxed max-w-2xl mx-auto space-y-2">
            <p>
              Thank you for using Bazaar Marketplace – The digital marketplace that reflects Egyptian user aspirations.
            </p>
            <p className="text-gray-500">
              All rights reserved © {new Date().getFullYear()} Bazaar Marketplace. All policies are subject to Egyptian laws and regulations.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.print()}
              className="px-6 py-2 bg-white border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-all duration-300"
            >
              Print Policies
            </button>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="px-6 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:opacity-90 transition-opacity duration-300"
            >
              Back to Top
            </button>
            <a
              href="/terms-conditions"
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300"
            >
              Terms of Use
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <a href="/privacy-policy" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            Privacy Policy
          </a>
          <a href="/refund-policy" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            Refund Policy
          </a>
          <a href="/shipping-policy" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            Shipping Policy
          </a>
          <a href="/faq" className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm">
            FAQ
          </a>
        </div>
      </div>
    </div>
  );
};

export default PoliciesPageEN;